import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import type { CompanionView } from '../ui/Companion'

/**
 * Who owns what is beside the page.
 *
 * The shell does, not the page. There is ONE band, and it is a band of the
 * application window: it collapses the nav to its rail and takes width from the
 * content column, none of which is a screen's business. A page says which views
 * it offers and which one to open at; where that lands is the shell's decision,
 * the same way `Page` owns width and the top bar owns the title.
 *
 * One band is the rule the archetype rests on. A contents, notes, sources,
 * comments and the assistant are the same kind of thing — what accompanies the
 * work rather than sitting in it — so they share one strip of width. A band
 * each would be several competing for the same edge, squeezing the page by
 * however many happened to be open, with no way to say which one was meant.
 *
 * It also means a page cannot mount two, and closing the band cannot leave the
 * shell collapsed around something that is no longer there.
 */

type CompanionApi = {
  /** The views on offer, in the order the strip draws them. */
  views: CompanionView[]
  /** Which view is open, and `null` when the band is shut. */
  active: string | null
  open: (id: string) => void
  close: () => void
  /** A page's views, for as long as that page is mounted. */
  declare: (views: CompanionView[]) => void
}

const CompanionContext = createContext<CompanionApi | null>(null)

export function CompanionProvider({ children }: { children: ReactNode }) {
  const [views, setViews] = useState<CompanionView[]>([])
  const [active, setActive] = useState<string | null>(null)

  /*
   * The three verbs are stable, and that is load-bearing rather than tidy: a
   * page declares its views from an effect, so a `close` whose identity moved
   * when the band opened would tear that effect down and shut the band on the
   * render that opened it.
   */
  const open = useCallback((id: string) => setActive(id), [])
  const close = useCallback(() => setActive(null), [])
  const declare = useCallback((next: CompanionView[]) => setViews(next), [])

  const api = useMemo<CompanionApi>(
    () => ({ views, active, open, close, declare }),
    [views, active, open, close, declare],
  )

  return (
    <CompanionContext.Provider value={api}>
      {children}
    </CompanionContext.Provider>
  )
}

export function useCompanion(): CompanionApi {
  const ctx = useContext(CompanionContext)
  if (!ctx) {
    throw new Error('useCompanion must be used inside the app shell.')
  }
  return ctx
}

/**
 * A page's own views, declared for as long as it is on screen.
 *
 * `views` must be memoised by the caller: it is an effect dependency, so an
 * array rebuilt on every render would re-declare on every render. Memoising it
 * against the data it is built from is also what keeps a count in the strip
 * honest without the page having to push one.
 *
 * A view a page does not offer is not drawn, and a view is only offered where
 * it opens onto something. An entry point onto nothing is a control teaching
 * somebody that it does nothing.
 *
 * A document is drawn outside the shell too — a shared link's public reading
 * copy has no band — so where there is none, declaring is a no-op.
 */
export function useCompanionViews(views: CompanionView[]) {
  const companion = useContext(CompanionContext)
  /* Held under a name of its own: a statement that OPENS with `declare` is read
     as a TypeScript declaration by the parser the tests run this file through,
     which then refuses the file and every module that imports it. */
  const declareViews = companion?.declare
  const close = companion?.close

  useEffect(() => {
    declareViews?.(views)
  }, [views, declareViews])

  /*
   * Leaving the page takes its views with it, and shuts the band: what it held
   * was about a page that is no longer here. Its own effect, so it fires when
   * the page unmounts rather than every time a count in the strip changes.
   */
  useEffect(() => {
    return () => {
      declareViews?.([])
      close?.()
    }
  }, [declareViews, close])
}

/**
 * A page's opener, so the call site reads as one line. `null` where no band
 * is drawn, so a control that would open one is not offered.
 */
export function useOpenCompanion(): ((id: string) => void) | null {
  const open = useContext(CompanionContext)?.open
  return useMemo(() => (open ? (id: string) => open(id) : null), [open])
}
